import { idbGetKeys } from '../../crypto-module/idb.js';
import { loadPatientKeypairFromIDB } from '../../crypto-module/load-patient-keypair-from-idb.js';

import type { BootstrapResult } from './bootstrap-result.type.js';

const RECOVERY_STARTED_TAG = 'recovery_started';
const RECOVERY_FINISHED_TAG = 'recovery_finished';

/**
 * Optionen für `bootstrapFromIdb`.
 *
 * `now` ist ein Wall-Clock-Snapshot für die `expiresAt`-Prüfung. Injektion
 * macht Tests deterministisch.
 *
 * Die frühere URL-Hash-Plausibilität ist mit dem Pin-im-Token entfallen
 * (siehe `docs/cancel-token-wire-format.md`). Pinning wird im Routing-Schritt
 * vor dem Recovery-Aufruf bereits gegen den dekodierten Token verifiziert.
 */
interface BootstrapFromIdbOptions {
  readonly now: number;
}

/**
 * Reload-Recovery-Bootstrap:
 *
 *  1. IDB lesen. Kein Record → `no-recovery`.
 *  2. `expiresAt`-Check via `loadPatientKeypairFromIDB`. Abgelaufen →
 *     `expired` (Cleanup ist intern bereits passiert).
 *  3. Sonst → `recovered` mit `tenantId` + `queueItemId` + `privateKey` für
 *     den Polling-Loop.
 *
 * Bewusst KEIN Pinning-Gate-Re-Run: der teure Schlüsselimport + SHA-256-
 * Vergleich entfällt, weil im Recovery-Pfad keine neuen Patientendaten in die
 * Pipeline gehen.
 *
 * Logging strikt ohne PII: nur die zwei Marker-Strings, keine queueItemId, kein
 * Fingerprint.
 */
export async function bootstrapFromIdb(options: BootstrapFromIdbOptions): Promise<BootstrapResult> {
  console.info(RECOVERY_STARTED_TAG);
  const result = await runBootstrap(options);
  console.info(RECOVERY_FINISHED_TAG);
  return result;
}

async function runBootstrap(options: BootstrapFromIdbOptions): Promise<BootstrapResult> {
  const recordExists = await hasIdbRecord();
  if (!recordExists) {
    return { kind: 'no-recovery' };
  }
  const loaded = await loadPatientKeypairFromIDB(options.now);
  if (loaded === null) {
    return { kind: 'expired' };
  }
  return {
    kind: 'recovered',
    tenantId: loaded.tenantId,
    queueItemId: loaded.queueItemId,
    publicKeyBase64: loaded.publicKeyBase64,
    privateKey: loaded.privateKey,
  };
}

async function hasIdbRecord(): Promise<boolean> {
  try {
    const record = await idbGetKeys();
    return record !== null;
  } catch {
    return false;
  }
}
